/**
 * @file Automated rules plugin for projection grids.
 *
 */

import _ from 'underscore';
import { RuleAuthoringView, RuleAuthoringModel } from '@bingads-webui-campaign/automated-rules';
import { supportedEntities, getRulesMenu } from './automated-rules-menu';

/**
 * Projection grid automated rules plugin,
 * User need to set these things in config
 * filterService: the filterService which can get current filter using getEditableFilter
 * automatedRules: {
 *   entityName: currently only support Campaign/AdGroup/Keyword/Ad
 *   i18n { getString }, localization setting
 *   i18nGlobal { getString }, localization setting
 *   router: { navigateToRoute }, specify the routing function
 *   scope: { campaignId, adgroupId } scope of the plugin
 *   instrumentation - instrumentation that already intilized
 *     and which should contains the senario of label and activity of createLabel
 *   state: { set, get, unset } module to store/fetch/update status for selected template
 *   stateMgr - the state manager which contains {getItem, setItem}
 *   preferenceSvc - preference service to store user preferences.
 *   appCfg - global app config
 *   bidRange - { FloorValue, CeilingValue} for bid inputs, needed for adgroup/keyword entity
 *   budgetRange - { FloorValue, CeilingValue} for budget inputs, needed for campaign entity
 *   permissions - permissions for the user
 *   accountTimeZone - accountTimeZone
 *   accountCurrencyCode - accountCurrencyCode
 *   currentUserId - currentUserId
 * @param { object } definePlugin:  function pointer used to define plugin for grid
 * @returns {void}
 */
export const automatedRulesPlugin = definePlugin => definePlugin('automatedRules', [
  'config',
  'gridView',
  'toolbar',
  'pagination',
  'crossPageSelectAll',
], (config, gridView, toolbar, pagination, crossPageSelectAll) => {
  const menuId = 'dropdown-automate';
  const toolbarEditor = toolbar.gridToolbarGroup.toolbarWithItem(menuId);

  let automatedRulesView;

  const actionStateKey = 'automated-rules-action-to-render';
  const {
    entityName,
    i18n,
    i18nGlobal,
    router,
    scope,
    state,
    instrumentation,
    stateMgr,
    preferenceSvc,
    appCfg,
    bidRange,
    budgetRange,
    alertCenter,
    accountCurrencyCode,
    accountTimeZone,
    permissions,
    currentUserId,
    odata,
    directive,
    walkMe,
    walkMeLink,
    walkThrus,
    isForVNext = false,
    newI18n,
    CampaignAdgroupSelectorEdmView,
    PredicateListReact,
    currentEntity,
  } = _.result(config.plugins, 'automatedRules', {});
  const { filterService } = config;

  function getSelectionInfo() {
    const info = {
      allPages: false,
      selectedRowCount: 0,
      selectedRowIds: null,
      campaignId: null,
      adGroupId: null,
    };

    const { campaignId, adGroupId } = scope;

    if (!_.isEmpty(campaignId)) {
      info.campaignId = parseInt(campaignId, 10);
    }
    if (!_.isEmpty(adGroupId)) {
      info.adGroupId = parseInt(adGroupId, 10);
    }

    if (crossPageSelectAll.isAllSelected) {
      _.extendOwn(info, {
        allPages: true,
        selectedRowIds: null,
        selectedRowCount: gridView.totalCountRows,
      });
    } else {
      _.extendOwn(info, {
        allPages: false,
        selectedRowIds: gridView.selectedItems().map(x => x.Id),
        selectedRowCount: gridView.selectedItems().length,
      });
    }

    return info;
  }

  function renderView(action, entity) {
    const info = getSelectionInfo();
    const model = new RuleAuthoringModel({
      odata,
      Entity: entity,
    });
    const ruleViewModel = model.get('SelectedRule');

    _.extendOwn(ruleViewModel, { entity, action, scopeInfo: info });
    model.set('SelectedRule', ruleViewModel);

    automatedRulesView = new RuleAuthoringView({
      isForVNext,
      grid: {
        gridView,
        pgridPlugins: {
          pagination,
          filterService,
          crossPageSelectAll,
        },
      },
      koModel: model,
      bidBudgetRange: entity === supportedEntities.Campaign ? budgetRange : bidRange,
      alertCenter,
      stateMgr,
      preferenceSvc,
      appCfg,
      accountTimeZone,
      accountCurrencyCode,
      currentUserId,
      instrumentation,
      i18n,
      i18nGlobal,
      permissions,
      scope,
      newI18n,
      CampaignAdgroupSelectorEdmView,
      PredicateListReact,
      currentEntity,
    });

    automatedRulesView.listenTo(gridView, 'didSelect', () => {
      const selectInfo = getSelectionInfo();

      automatedRulesView.updateSelectedEntityCount(selectInfo);
    });

    automatedRulesView.render();
    if (entityName === supportedEntities.Keyword) {
      automatedRulesView.addWalkme({
        directive,
        walkMe,
        walkMeLink,
        walkThrus,
      });
    }

    automatedRulesView.$el.find(':input:enabled:first').focus();
  }

  function removeView() {
    if (automatedRulesView) {
      automatedRulesView.remove();
      automatedRulesView = null;
    }
  }

  function renderRuleAuthoringView(actionId) {
    return () => {
      // get action from actionID, the format is '{0}_{1}_{2}_{3}',
      // like 'CampaignsGrid_automateRule_Campaign_1'
      // {2} is the rule template's entitylevel and {3} is the rule template id.
      const selectedInfo = actionId.split('_');
      const selectedTemplateId = selectedInfo[selectedInfo.length - 1];

      if (selectedTemplateId === 'RedirectToREManagement') {
        router.navigateToRoute('bulk_rules');
        return;
      }
      const selectedTemplateEntityLevel = selectedInfo[selectedInfo.length - 2];

      if (automatedRulesView) {
        throw new Error('duplicate automated rules view.');
      } else {
        renderView(selectedTemplateId, selectedTemplateEntityLevel);
      }

      // use defer here to wait for the this dropdown menu to hide first
      _.defer(() => {
        const gridContainer = gridView.$el.closest('.bingadsgrid');
        const editorContainer = gridContainer.find('.editor__container');
        const gridToolbar = gridContainer.find('.grid-toolbar');

        if (isForVNext) {
          toolbarEditor.overrideWith(automatedRulesView);
        } else {
          editorContainer.html(automatedRulesView.$el);
          // show editor
          editorContainer.show();
        }
        // hide the toolbar with empty view, which will have blank height
        gridToolbar.hide();

        toolbar.gridToolbarGroup.hideAllToolbars();

        // show toolbar after hide the automated rules form
        automatedRulesView.once('hide', () => {
          gridToolbar.show();
          editorContainer.hide();
          toolbar.gridToolbarGroup.showAllToolbars();
          automatedRulesView = null;

          if (isForVNext) {
            toolbarEditor.overrideWith(null);
          }
        });
      });
    };
  }

  function switchTab(actionId) {
    return () => {
      const selectedMenu = actionId.split('_');
      const entityLevel = selectedMenu[selectedMenu.length - 2];

      const scenario = instrumentation[`authorRule${entityLevel}`].create();

      scenario.createRule.create().trace(`[AutomatedRulesRouting] User switching tabs via automated rules menu to ${actionId} on ${entityLevel} grid`);

      state.set(actionStateKey, actionId);
      switch (entityLevel) {
        case supportedEntities.Campaign:
          router.navigateToRoute('campaign');
          break;
        case supportedEntities.AdGroup:
          router.navigateToRoute('campaign_adgroup');
          break;
        case supportedEntities.Ad:
          router.navigateToRoute('adgroup_ad');
          break;
        case supportedEntities.Keyword:
        default:
          router.navigateToRoute('adgroup_keyword');
          break;
      }
    };
  }

  const selectInfo = getSelectionInfo();
  const shouldBeDisabled = isForVNext && (permissions.IsReadOnlyUser || appCfg.get('CurrentAccountInActive'));

  toolbarEditor.update(getRulesMenu(
    entityName,
    renderRuleAuthoringView, switchTab, selectInfo, i18n, isForVNext, shouldBeDisabled,
    false
  ));

  gridView.on('didReload', () => {
    const actionId = state.get(actionStateKey);

    if (actionId) {
      state.unset(actionStateKey, { silent: true });
      renderRuleAuthoringView(actionId)();
    }
  });

  return {
    remove: () => {
      removeView();
    },
  };
});
